import { useEffect, useState } from "react";
import { IoSearchOutline } from "react-icons/io5";
import LoadingSpinner from "../../../components/elements/LoadingSpinner";
import Pagination from "../../../components/Pagination";
import { FaRegStar } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import OrderModal from "../../../components/modals/Restaurant/OrderModal";
import MealModal from "../../../components/modals/Restaurant/MealModal";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";
import {
  getAllRestaurantOrders,
  getRestaurantOrderDetails,
  clearCurrentOrderDetails,
  RestaurantOrderItem,
} from "../../../redux/restaurantOrders/restaurantOrdersSlice";

// MenuItem interface matching MealType from MealModal
interface MenuItem {
  id: number;
  name: string;
  category: string;
  restaurant: string;
  description: string;
  image: string;
  lastUpdated: string;
  price: number;
  rating: number;
  orders: number;
  status: string;
}

type TableType = "orders" | "menu";
type RowData = RestaurantOrderItem | MenuItem;

const isOrder = (data: RowData): data is RestaurantOrderItem =>
  (data as RestaurantOrderItem).orderNumber !== undefined;

const OrderAndMenuTable = ({ table }: { table: TableType }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { orders, currentOrderDetails, status, detailsStatus, error } =
    useAppSelector((state: RootState) => state.restaurantOrders);

  const [menuData, setMenuData] = useState<MenuItem[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<RestaurantOrderItem | null>(null);
  const [selectedMeal, setSelectedMeal] = useState<MenuItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [restaurantFilter, setRestaurantFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch orders when table is "orders"
  useEffect(() => {
    if (table === "orders") {
      dispatch(
        getAllRestaurantOrders({
          page: 1,
          limit: 100,
        })
      );
    }
  }, [dispatch, table]);

  // Fetch menu data when table is "menu" - now using real API
  useEffect(() => {
    if (table === "menu") {
      // Navigate to the new dedicated meals page
      navigate('/dashboard/restaurants/meals');
    }
  }, [table, navigate]);

  // Fetch order details when an order is selected
  useEffect(() => {
    if (selectedOrderId) {
      dispatch(getRestaurantOrderDetails(selectedOrderId));
    }
  }, [dispatch, selectedOrderId]);

  // Get the data based on table type
  const getData = (): RowData[] => {
    if (table === "orders") {
      return orders;
    }
    return menuData;
  };

  // Apply filters to the data
  const getFilteredData = (): RowData[] => {
    let data = getData();

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      data = data.filter((item) => {
        if (isOrder(item)) {
          return (
            (item.orderNumber &&
              item.orderNumber.toLowerCase().includes(searchLower)) ||
            (item.orderId &&
              item.orderId.toLowerCase().includes(searchLower)) ||
            (item.customerName &&
              item.customerName.toLowerCase().includes(searchLower)) ||
            (item.riderName &&
              item.riderName.toLowerCase().includes(searchLower))
          );
        } else {
          return (
            item.name.toLowerCase().includes(searchLower) ||
            item.restaurant.toLowerCase().includes(searchLower)
          );
        }
      });
    }

    // Apply status filter
    if (statusFilter !== "all") {
      data = data.filter((item) => {
        if (isOrder(item)) {
          return item.status?.toLowerCase() === statusFilter.toLowerCase();
        } else {
          return item.status?.toLowerCase() === statusFilter.toLowerCase();
        }
      });
    }

    return data;
  };

  const filteredData = getFilteredData();
  
  // Pagination logic
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const handlePagination = (page: number) => {
    setCurrentPage(page);
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);
  
  const currentIsLoading =
    table === "orders" ? status === "loading" : isLoading;
  const currentError = table === "orders" ? error : null;

  const handleViewOrder = (order: RestaurantOrderItem) => {
    setSelectedOrderId(order.orderId);
    setSelectedOrder(order);
  };

  const handleCloseModal = () => {
    setSelectedOrderId(null);
    setSelectedOrder(null);
    dispatch(clearCurrentOrderDetails());
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return (
      date.toLocaleDateString() +
      " " +
      date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    );
  };

  const formatETA = (etaString: string) => {
    const eta = new Date(etaString);
    const now = new Date();
    const diff = eta.getTime() - now.getTime();
    const minutes = Math.max(0, Math.floor(diff / (1000 * 60)));
    return minutes > 0 ? `${minutes} mins` : "Now";
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "delivered":
        return "bg-[#008B501F] text-secondary";
      case "pending":
      case "preparing":
        return "text-yellow-600 bg-yellow-100";
      case "cancelled":
        return "text-red-500 bg-[#EA19191F]";
      case "ready":
        return "text-blue-600 bg-blue-100";
      case "in-progress":
        return "text-orange-600 bg-orange-100";
      case "available":
        return "bg-[#008B501F] text-secondary";
      case "unavailable":
        return "text-red-500 bg-[#EA19191F]";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const renderTableHeader = () =>
    table === "orders" ? (
      <>
        <th className="p-3 font-medium">Order Number</th>
        <th className="p-3 font-medium">Customer</th>
        <th className="p-3 font-medium">Rider</th>
        <th className="p-3 font-medium">Total</th>
        <th className="p-3 font-medium">Status</th>
        <th className="p-3 font-medium">ETA</th>
        <th className="p-3 font-medium">Order Date</th>
        <th className="p-3 font-medium">Actions</th>
      </>
    ) : (
      <>
        <th className="p-3 font-medium">Item</th>
        <th className="p-3 font-medium">Restaurant</th>
        <th className="p-3 font-medium">Orders</th>
        <th className="p-3 font-medium">Rating</th>
        <th className="p-3 font-medium">Status</th>
        <th className="p-3 font-medium">Price</th>
        <th className="p-3 font-medium">Actions</th>
      </>
    );

  const renderRow = (row: RowData) => {
    if (!row) return null;
    
    return isOrder(row) ? (
      <tr key={row.orderId} className="border text-sm hover:bg-gray-50">
        <td className="p-5">
          <span className="font-medium">{row.orderNumber || "N/A"}</span>
        </td>
        <td className="p-5">{row.customerName || "Anonymous"}</td>
        <td className="p-5">{row.riderName || "Unassigned"}</td>
        <td className="p-5">₦{(row.totalAmount || 0).toLocaleString()}</td>
        <td className="p-5">
          <span
            className={`${getStatusColor(
              row.status || ""
            )} text-center rounded-md p-2 text-xs font-medium capitalize`}
          >
            {row.status || "Unknown"}
          </span>
        </td>
        <td className="p-5">
          <span className="text-sm">{row.eta ? formatETA(row.eta) : "N/A"}</span>
        </td>
        <td className="p-5">
          <span className="text-xs text-gray-500">
            {row.orderDate ? formatDate(row.orderDate) : "N/A"}
          </span>
        </td>
        <td className="p-2">
          <button
            className="text-secondary border border-secondary rounded-md px-4 py-2 flex items-center gap-2 text-xs hover:bg-secondary hover:text-white transition-colors"
            onClick={() => handleViewOrder(row)}
            disabled={
              detailsStatus === "loading" && selectedOrderId === row.orderId
            }
          >
            {detailsStatus === "loading" && selectedOrderId === row.orderId
              ? "Loading..."
              : "View details"}
          </button>
        </td>
      </tr>
    ) : (
      <tr key={row.id} className="border text-sm">
        <td className="p-5">
          <div className="flex items-center gap-2">
            <img
              src={row.image || "/assets/avatar.svg"}
              alt={row.name || "Item"}
              className="w-12 h-12 object-cover rounded-md"
            />
            {row.name || "Unknown Item"}
          </div>
        </td>
        <td className="p-5">{row.restaurant || "N/A"}</td>
        <td className="p-5">{row.orders || 0}</td>
        <td className="p-5">
          <div className="flex items-center gap-1">
            {row.rating || 0} <FaRegStar className="text-orange-400" />
          </div>
        </td>
        <td className="p-5">
          <span
            className={`${getStatusColor(
              row.status || ""
            )} text-center rounded-md p-2 text-xs font-medium capitalize`}
          >
            {row.status || "Unknown"}
          </span>
        </td>
        <td className="p-5">₦{(row.price || 0).toLocaleString()}</td>
        <td className="p-5">
          <button
            className="text-secondary border border-secondary rounded-md px-2 flex items-center text-xs hover:bg-secondary hover:text-white transition-colors"
            onClick={() => setSelectedMeal(row)}
          >
            View details
          </button>
        </td>
      </tr>
    );
  };

  return (
    <div className="w-full">
      {/* Search and Filter Section */}
      <div className="my-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        {/* Search Input */}
        <div className="relative flex-1 max-w-md">
          <IoSearchOutline className="w-5 h-5 absolute top-[0.6rem] left-3 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 pl-10 text-sm w-full focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary"
            placeholder={`Search ${table}...`}
          />
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
          >
            <option value="all">All Status</option>
            {table === "orders" ? (
              <>
                <option value="pending">Pending</option>
                <option value="preparing">Preparing</option>
                <option value="ready">Ready</option>
                <option value="in-progress">In Progress</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </>
            ) : (
              <>
                <option value="available">Available</option>
                <option value="unavailable">Unavailable</option>
              </>
            )}
          </select>
        </div>

        {/* Clear Filters Button */}
        {(searchTerm || statusFilter !== "all") && (
          <button
            onClick={() => {
              setSearchTerm("");
              setStatusFilter("all");
              setRestaurantFilter("all");
            }}
            className="text-sm text-secondary hover:text-secondary-dark underline"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className="min-h-[500px]">
        <table className="w-full">
          <thead className="font-normal">
            <tr className="bg-gray-100 text-left">{renderTableHeader()}</tr>
          </thead>
          <tbody>
            {currentIsLoading ? (
              <tr>
                <td colSpan={8} className="text-center py-8">
                  <LoadingSpinner />
                </td>
              </tr>
            ) : currentError ? (
              <tr>
                <td colSpan={8} className="text-center py-8 text-red-500">
                  Error loading {table}: {currentError}
                </td>
              </tr>
            ) : paginatedData.length > 0 ? (
              paginatedData.map(renderRow)
            ) : (
              <tr>
                <td colSpan={8} className="text-center py-8 text-gray-500">
                  {searchTerm || statusFilter !== "all"
                    ? `No ${table} match your filters.`
                    : `No ${table} found.`}
                  {(searchTerm || statusFilter !== "all") && (
                    <div className="mt-2">
                      <button
                        onClick={() => {
                          setSearchTerm("");
                          setStatusFilter("all");
                          setRestaurantFilter("all");
                        }}
                        className="text-secondary hover:underline text-sm"
                      >
                        Clear filters to see all {table}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredData.length > 0 && (
        <div className="mt-6">
          <Pagination
            itemsPerPage={itemsPerPage}
            length={totalItems}
            currentPage={currentPage}
            handlePagination={handlePagination}
            prevPage={prevPage}
            nextPage={nextPage}
          />
        </div>
      )}

      {/* Modals */}
      {selectedOrderId && currentOrderDetails && selectedOrder && (
        <OrderModal
          toggleModal={handleCloseModal}
          order={currentOrderDetails}
          isSupportOpen={selectedOrder.isSupportOpen}
        />
      )}
      {selectedMeal && (
        <MealModal
          toggleModal={() => setSelectedMeal(null)}
          meal={selectedMeal}
        />
      )}
    </div>
  );
};

export default OrderAndMenuTable;
